import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {onSessionCleared} from '../../lib/sessionCleanup';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import type {Project} from '../../domain/types';
import {projectDeleteBlockedReason} from '../../domain/projects';
import * as projectPreferences from '../../lib/projectPreferences';
import type {ProjectMetadata} from '../../lib/projectPreferences';
import {debugLog, debugWarn} from '../../lib/debug';

interface WorkerProject {
    id: string;
    user_id: string;
    name: string;
    description: string | null;
    created_at: string;
    updated_at: string;
    totalTranscriptions: number;
    defaultWordlistName: string;
    default_wordlist_id?: string;
    is_global: boolean; // the default project. Backend still names it is_global; maps to Project.isDefault
}

// Store ETag for projects.
// Module-level, so it outlives a sign-out — the next account would send the previous account's
// If-None-Match, and a 304 would hand it whatever is in the store. Dropped on sign-out.
let projectsEtag: string | null = null;

onSessionCleared(() => {
  projectsEtag = null;
});

export const fetchProjects = createAsyncThunk(
  'projects/fetch',
  async (_, {rejectWithValue, getState, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/projects`;

      debugLog('Making GET request to:', url);
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${jwt}`,
      };

            // Add If-None-Match header if we have a stored ETag
      if (projectsEtag) {
        debugLog('Sending If-None-Match:', projectsEtag);
        headers['If-None-Match'] = projectsEtag;
      } else {
        debugLog('No stored ETag - making fresh request');
      }

      const response = await apiFetch(url, {headers});

      debugLog('Response status:', response.status);

            // Extract ETag from response if present
      const newEtag = response.headers.get('etag');
      if (newEtag) {
        const etag = newEtag.startsWith('W/') ? newEtag.slice(2) : newEtag;
        debugLog('ETag:', newEtag);
        projectsEtag = etag;
      }

            // Handle 304 Not Modified - return cached data
      if (response.status === 304) {
        debugLog('304 Not Modified - data unchanged');
        const state = getState() as any;
        // The slice field is `data`, not `projects` — reading the wrong one returned undefined and
        // emptied the project list on any 304.
        return state.projects.data;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Worker API returned error ${response.status}: ${errorText}`);
      }

            // Parse response
            interface ProjectsResponse {
                projects: WorkerProject[];
            }

            const data: ProjectsResponse = await response.json();
            debugLog('Successfully parsed', data.projects.length, 'projects');

            // Get current Redux state to preserve favorites and lastActivity
            const state = getState() as any;
            const existingProjects = state.projects.data as Project[];
            const storedMetadata = projectPreferences.getProjectMetadata();

            const projects: Project[] = data.projects.map((wp) => {
              const existingProject = existingProjects.find(ep => ep.id === wp.id);
              const metadata = storedMetadata.find(m => m.projectId === wp.id);

              return {
                id: wp.id,
                name: wp.name,
                description: wp.description || '',
                createdAt: wp.created_at,
                updatedAt: wp.updated_at,
                defaultBeepSoundId: undefined,
                defaultWordlistId: wp.default_wordlist_id,
                totalTranscriptions: wp.totalTranscriptions || 0,
                defaultWordlistName: wp.defaultWordlistName || '',
                // Favourite/lastActivity are not on the project record yet, so prefer whatever is
                // already in Redux and fall back to local persistence.
                lastActivity: existingProject?.lastActivity ?? metadata?.lastActivity ?? undefined,
                favorite: existingProject?.favorite ?? metadata?.favorite ?? false,
                isDefault: wp.is_global,
              };
            });

            return projects;
    } catch (error) {
      const errorStr = String(error);

            // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(errorStr);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/create',
  async ({name, description}: { name: string; description?: string }, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/projects`;

      debugLog('Making POST request to:', url);
      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description: description || null,
        }),
      });

      debugLog('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Worker API returned error ${response.status}: ${errorText}`);
      }

            interface CreateProjectResponse {
                project: WorkerProject;
            }

            const data: CreateProjectResponse = await response.json();
            debugLog('Created project:', data.project);

            // Clear ETag to force fresh fetch next time
            projectsEtag = null;

            // Convert to local format
            const project: Project = {
              id: data.project.id,
              name: data.project.name,
              description: data.project.description || '',
              createdAt: data.project.created_at,
              updatedAt: data.project.updated_at,
              defaultBeepSoundId: undefined,
              defaultWordlistId: data.project.default_wordlist_id,
              defaultWordlistName: data.project.defaultWordlistName || '',
              totalTranscriptions: data.project.totalTranscriptions,
              lastActivity: undefined,
              favorite: false,
              isDefault: data.project.is_global,
            };

            return project;
    } catch (error) {
            // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

export const updateProjectThunk = createAsyncThunk(
  'projects/update',
  async ({id, name, description}: { id: string; name: string; description?: string }, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/projects/${id}`;

      debugLog('Making PUT request to:', url);
      const response = await apiFetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description: description || null,
        }),
      });

      debugLog('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Worker API returned error ${response.status}: ${errorText}`);
      }

            interface UpdateProjectResponse {
                project: WorkerProject;
            }

            const data: UpdateProjectResponse = await response.json();
            debugLog('Updated project:', data.project);

            // Clear ETag to force fresh fetch next time
            projectsEtag = null;

            // Convert to local format
            const project: Project = {
              id: data.project.id,
              name: data.project.name,
              description: data.project.description || '',
              createdAt: data.project.created_at,
              updatedAt: data.project.updated_at,
              defaultBeepSoundId: undefined,
              defaultWordlistId: data.project.default_wordlist_id,
              defaultWordlistName: data.project.defaultWordlistName || '',
              totalTranscriptions: data.project.totalTranscriptions || 0,
              lastActivity: undefined,
              favorite: false,
              isDefault: data.project.is_global,
            };

            return project;
    } catch (error) {
            // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

export const deleteProjectThunk = createAsyncThunk(
  'projects/delete',
  async (projectId: string, {rejectWithValue, dispatch, getState}) => {
    // The rule lives here as well as in the UI: a project the user cannot afford to lose must not
    // go just because a screen forgot to hide its own button. Unknown ids fall through to the
    // request and let the backend answer.
    const projects = (getState() as {projects: {data: Project[]}}).projects.data;
    const target = projects.find(p => p.id === projectId);
    if (target) {
      const blockedReason = projectDeleteBlockedReason(target, projects);
      if (blockedReason) {
        debugWarn('Refusing to delete project:', blockedReason);
        return rejectWithValue(blockedReason);
      }
    }

    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/projects/${projectId}`;

      debugLog('Making DELETE request to:', url);
      const response = await apiFetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      debugLog('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Worker API returned error ${response.status}: ${errorText}`);
      }

            // Clear ETag to force fresh fetch next time
      projectsEtag = null;
      debugLog('Project deleted successfully');

      return projectId;
    } catch (error) {
            // Check for 401/auth errors and auto-logout
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

export const setDefaultWordlistThunk = createAsyncThunk(
  'projects/setDefaultWordlist',
  async ({projectId, wordlistId}: { projectId: string; wordlistId: string }, {rejectWithValue, dispatch}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/projects/${projectId}/default-wordlist`;

      debugLog('Making POST request to set default wordlist:', url);
      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wordlistId,
        }),
      });

      debugLog('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);

        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }

        throw new Error(`Worker API returned error ${response.status}: ${errorText}`);
      }

      interface UpdateProjectResponse {
        project: WorkerProject;
      }

      const data: UpdateProjectResponse = await response.json();
      debugLog('Updated project default wordlist:', data.project);

      // Clear ETag to force fresh fetch next time
      projectsEtag = null;

      // Convert to local format
      const project: Project = {
        id: data.project.id,
        name: data.project.name,
        description: data.project.description || '',
        createdAt: data.project.created_at,
        updatedAt: data.project.updated_at,
        defaultBeepSoundId: undefined,
        defaultWordlistId: data.project.default_wordlist_id,
        defaultWordlistName: data.project.defaultWordlistName || '',
        totalTranscriptions: data.project.totalTranscriptions || 0,
        lastActivity: undefined,
        favorite: false,
        isDefault: data.project.is_global,
      };

      return project;
    } catch (error) {
      handleAuthFailure(error, dispatch);

      return rejectWithValue(String(error));
    }
  }
);

export const setCurrentProjectThunk = createAsyncThunk(
  'projects/setCurrent',
  async (project: Project | null, {rejectWithValue}) => {
    try {
      // On the web the current project is really carried by the URL; this is the
      // "where was I last" fallback for a cold load.
      projectPreferences.setCurrentProjectId(project?.id || null);
      debugLog('Set current project:', project?.id);

      return project;
    } catch (error) {
      console.error('Failed to persist current project:', error);
      return rejectWithValue(String(error));
    }
  }
);

export const loadProjectMetadata = createAsyncThunk(
  'projects/loadMetadata',
  async (_, {rejectWithValue}) => {
    try {
      return projectPreferences.getProjectMetadata();
    } catch (error) {
      console.error('Failed to load project metadata:', error);
      return rejectWithValue(String(error));
    }
  }
);

export const persistProjectMetadata = createAsyncThunk(
  'projects/persistMetadata',
  async ({projectId, favorite, lastActivity}: {projectId: string; favorite?: boolean; lastActivity?: string}, {rejectWithValue}) => {
    try {
      const metadata: ProjectMetadata = projectPreferences.updateProjectMetadata(projectId, {
        favorite,
        lastActivity,
      });
      return metadata;
    } catch (error) {
      console.error('Failed to persist project metadata:', error);
      return rejectWithValue(String(error));
    }
  }
);
